import * as format from '@app/utils/formatters';

const sanitizeName = (name = '') => name.replaceAll('/', '_');

const ASSETS_FRAMES_URL = 'https://assets.rmdevops.com/broadcast-frames/';
const S3_FRAMES_URL =
    'https://s3.amazonaws.com/sports-universal-frame-extraction/';

const fetchBlob = async (url: string): Promise<Blob | null> => {
    try {
        const response = await fetch(url, { cache: 'no-cache' });
        if (!response.ok) return null;
        return await response.blob();
    } catch {
        return null;
    }
};

/**
 * Fetches a single broadcast frame as a Blob.
 *
 * Do NOT add custom request headers here. A custom header (for example
 * `Cache-Control`) turns the GET into a non-simple CORS request and forces
 * an OPTIONS preflight, which the assets host rejects with a 403. The
 * `cache: 'no-cache'` fetch option achieves the same revalidation without
 * triggering a preflight.
 *
 * The assets CDN sometimes serves a cached frame without CORS headers, so the
 * fetch fails even though the image renders. Those frames are retried on the
 * S3 bucket behind the CDN, which always answers with CORS headers.
 * ponytail: client-side fallback; the real fix is a CDN cache keyed on Origin.
 *
 * Returns `null` instead of throwing so callers can skip a failed frame and
 * keep the rest of the download going.
 */
export const fetchFrameBlob = async (
    frameUrl?: string | null,
): Promise<Blob | null> => {
    if (!frameUrl) return null;
    const blob = await fetchBlob(frameUrl);
    if (blob || !frameUrl.startsWith(ASSETS_FRAMES_URL)) return blob;
    return fetchBlob(S3_FRAMES_URL + frameUrl.slice(ASSETS_FRAMES_URL.length));
};

export const getFrameFolderName = (
    broadcastName = '',
    contentDate?: string | Date,
) =>
    sanitizeName(
        broadcastName.replace(
            /\([0-9]{2}-[0-9]{2}-[0-9]{4}\)/g,
            `(${format.shortDate(contentDate)})`,
        ),
    );

export const getFrameFileName = ({
    rightsholder,
    sponsor,
    placement,
}: {
    rightsholder?: { name?: string };
    sponsor?: { name?: string };
    placement?: { name?: string };
}) =>
    `${sanitizeName(rightsholder?.name)}_${sanitizeName(
        sponsor?.name,
    )}_${sanitizeName(placement?.name)}.jpeg`;

export const saveBlob = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.download = filename;
    link.href = url;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }, 1000);
};

// Safari keeps only the last of several downloads started together, so
// multi-file downloads are spaced out.
// ponytail: fixed 1s gap, tune here if Safari still drops files.
export const DOWNLOAD_GAP_MS = 1000;

const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Saves each source image as-is (no re-encode, so full resolution) as its own
 * file, one after another. Failed images are skipped. Resolves to the number
 * of failures.
 */
export const downloadImages = async (
    images: { url?: string | null; filename: string }[],
): Promise<number> => {
    const blobs = await Promise.all(
        images.map(({ url }) => fetchFrameBlob(url)),
    );
    let saved = 0;
    for (const [i, blob] of blobs.entries()) {
        if (!blob) continue;
        if (saved > 0) await wait(DOWNLOAD_GAP_MS);
        saveBlob(blob, images[i].filename);
        saved += 1;
    }
    return images.length - saved;
};
